import React, { useState, useEffect, useCallback } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Box,
  Chip,
  IconButton,
  Divider,
  Button,
  Badge,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Popover,
} from '@mui/material';
import {
  AccountCircle,
  Logout,
  Business,
  Person,
  AdminPanelSettings,
  Assessment,
  Notifications,
  Circle,
  SupervisorAccount,
  ClearAll,
  Menu as MenuIcon,
  LibraryBooks,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { hasPortalAccess } from '../portals/shared/PortalGuard';
import { authFetch } from '../api/authFetch';
import kpmgLogo from '../assets/kpmg-logo.png';
import PortalTooltip from './PortalTooltip';

interface NotificationItem {
  id: string;
  title: string;
  message: string;
  is_read: boolean;
  created_at: string;
}

interface NavbarProps {
  userRole?: string | null;
  roleDisplayName?: string | null;
  secondaryRoles?: string[];
}

const Navbar: React.FC<NavbarProps> = ({ userRole, roleDisplayName, secondaryRoles = [] }) => {
  const { user, organizationData, logout, loading } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifAnchorEl, setNotifAnchorEl] = useState<null | HTMLElement>(null);
  const [portalMenuAnchorEl, setPortalMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const routerNavigate = useNavigate();
  const location = useLocation();

  // Determine notification endpoint based on active portal
  const getNotificationEndpoint = useCallback(() => {
    if (location.pathname.startsWith('/domain-management')) return '/api/admin/portal/asset-notifications';
    if (location.pathname.startsWith('/admin')) return '/api/admin/notifications';
    if (location.pathname.startsWith('/sme') || location.pathname.startsWith('/em')) return '/api/sme/notifications';
    return '/api/client/notifications';
  }, [location.pathname]);

  const getMarkReadEndpoint = useCallback((id: string) => {
    if (location.pathname.startsWith('/domain-management')) return `/api/admin/portal/asset-notifications/${id}/read`;
    if (location.pathname.startsWith('/admin')) return `/api/admin/notifications/${id}/read`;
    if (location.pathname.startsWith('/sme') || location.pathname.startsWith('/em')) return `/api/sme/notifications/${id}/read`;
    return `/api/client/notifications/${id}/read`;
  }, [location.pathname]);

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await authFetch(getNotificationEndpoint());
      const data = await response.json();
      if (data.success) {
        setNotifications(data.notifications || []);
      }
    } catch {
      // Notifications are best-effort; do not block UI
    }
  }, [getNotificationEndpoint]);

  useEffect(() => {
    if (user) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 60000); // Poll every 60s
      return () => clearInterval(interval);
    }
  }, [user, fetchNotifications]);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const handleNotifOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotifAnchorEl(event.currentTarget);
  };
  const handleNotifClose = () => setNotifAnchorEl(null);

  const markAsRead = async (id: string) => {
    try {
      await authFetch(getMarkReadEndpoint(id), { method: 'PUT' });
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
      );
    } catch {
      // Best-effort
    }
  };

  const clearAllNotifications = async () => {
    try {
      let clearUrl: string;
      if (location.pathname.startsWith('/domain-management')) clearUrl = '/api/admin/portal/asset-notifications/clear';
      else if (location.pathname.startsWith('/admin')) clearUrl = '/api/admin/notifications/clear';
      else if (location.pathname.startsWith('/sme') || location.pathname.startsWith('/em')) clearUrl = '/api/sme/notifications/clear';
      else clearUrl = '/api/client/notifications/clear';
      await authFetch(clearUrl, { method: 'DELETE' });
      setNotifications([]);
    } catch {
      // Best-effort
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = async () => {
    handleMenuClose();
    await logout();
  };

  // Determine active section from URL path
  const getActiveSection = (): 'client' | 'sme' | 'em' | 'admin' | 'domain_management' => {
    if (location.pathname.startsWith('/domain-management')) return 'domain_management';
    if (location.pathname.startsWith('/admin')) return 'admin';
    if (location.pathname.startsWith('/em')) return 'em';
    if (location.pathname.startsWith('/sme')) return 'sme';
    return 'client';
  };
  const activeSection = getActiveSection();

  // KPMG brand colors per portal
  const PORTAL_COLORS: Record<string, string> = {
    client: '#00338D',  // KPMG Dark Blue
    sme: '#483698',     // KPMG Purple
    em: '#C6007E',      // KPMG Dark Pink
    admin: '#00B8F1',   // KPMG Light Blue
    domain_management: '#098E7E',  // KPMG Dark Green
  };
  const navbarBgColor = PORTAL_COLORS[activeSection] || '#00338D';

  const navigateTo = (path: string) => {
    handleMenuClose();
    setPortalMenuAnchorEl(null);
    routerNavigate(path);
  };

  const getInitials = (name: string): string =>
    name.split(' ').map((w) => w.charAt(0)).join('').toUpperCase().slice(0, 2);

  // Build list of portal tabs the user can access
  const portalTabs: { label: string; path: string; portal: string; icon: React.ReactNode; tooltipKey: string }[] = [];
  if (hasPortalAccess(userRole || null, 'sme', secondaryRoles))
    portalTabs.push({ label: 'KPMG SME', path: '/sme', portal: 'sme', icon: <Assessment />, tooltipKey: 'navbar.tab.sme' });
  if (hasPortalAccess(userRole || null, 'em', secondaryRoles))
    portalTabs.push({ label: 'KPMG EM/Lead', path: '/em', portal: 'em', icon: <Assessment />, tooltipKey: 'navbar.tab.em' });
  if (hasPortalAccess(userRole || null, 'domain_management', secondaryRoles))
    portalTabs.push({ label: 'Asset Mgmt', path: '/domain-management', portal: 'domain_management', icon: <LibraryBooks />, tooltipKey: 'navbar.tab.domain_management' });
  if (hasPortalAccess(userRole || null, 'admin', secondaryRoles))
    portalTabs.push({ label: 'KPMG Admin', path: '/admin', portal: 'admin', icon: <AdminPanelSettings />, tooltipKey: 'navbar.tab.admin' });

  return (
    <AppBar
      position="fixed"
      elevation={1}
      sx={{
        backgroundColor: navbarBgColor,
        transition: 'background-color 0.3s ease',
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar>
        {/* Logo */}
        <PortalTooltip tooltipKey="navbar.logo" placement="bottom">
          <Box
            component="img"
            src={kpmgLogo}
            alt="KPMG"
            sx={{ height: 36, mr: 1.5 }}
          />
        </PortalTooltip>

        {/* App title — always visible */}
        <PortalTooltip tooltipKey="navbar.title" placement="bottom">
          <Typography
            variant="h6"
            component="div"
            noWrap
            sx={{
              fontWeight: 600,
              mr: 2,
              fontSize: { xs: '0.85rem', md: '1rem' },
              display: { xs: 'none', sm: 'block' },
            }}
          >
            Discovery IQ Agent
          </Typography>
        </PortalTooltip>

        {/* Portal tabs — inline on md+, hamburger on smaller screens */}
        {portalTabs.length > 0 && (
          <>
            {/* Inline tabs (md and up) */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.5, flexGrow: 1 }}>
              {portalTabs.map((tab) => (
                <PortalTooltip key={tab.portal} tooltipKey={tab.tooltipKey} placement="bottom">
                  <Button
                    color="inherit"
                    size="small"
                    startIcon={tab.icon}
                    onClick={() => navigateTo(tab.path)}
                    sx={{
                      textTransform: 'none',
                      fontSize: '0.85rem',
                      borderBottom: activeSection === tab.portal ? '2px solid white' : '2px solid transparent',
                      backgroundColor: activeSection === tab.portal ? 'rgba(255,255,255,0.15)' : 'transparent',
                      borderRadius: 0,
                      px: 1.5,
                    }}
                  >
                    {tab.label}
                  </Button>
                </PortalTooltip>
              ))}
            </Box>

            {/* Hamburger menu (below md) */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, flexGrow: 1 }}>
              <IconButton color="inherit" onClick={(e) => setPortalMenuAnchorEl(e.currentTarget)}>
                <MenuIcon />
              </IconButton>
              <Menu
                anchorEl={portalMenuAnchorEl}
                open={Boolean(portalMenuAnchorEl)}
                onClose={() => setPortalMenuAnchorEl(null)}
              >
                {portalTabs.map((tab) => (
                  <MenuItem
                    key={tab.portal}
                    selected={activeSection === tab.portal}
                    onClick={() => navigateTo(tab.path)}
                  >
                    <ListItemIcon sx={{ color: 'inherit' }}>{tab.icon}</ListItemIcon>
                    <ListItemText>{tab.label}</ListItemText>
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          </>
        )}

        {/* Spacer when no portal tabs */}
        {portalTabs.length === 0 && <Box sx={{ flexGrow: 1 }} />}

        {/* Organization chip */}
        {organizationData && (
          <PortalTooltip tooltipKey="navbar.org_chip" placement="bottom">
            <Chip
              icon={<Business />}
              label={organizationData.organizationName}
              variant="outlined"
              size="small"
              sx={{
                mr: 2,
                color: 'white',
                borderColor: 'rgba(255,255,255,0.4)',
                '& .MuiChip-icon': { color: 'white' },
              }}
            />
          </PortalTooltip>
        )}

        {/* Notification bell */}
        <PortalTooltip tooltipKey="navbar.notifications" placement="bottom">
          <IconButton color="inherit" onClick={handleNotifOpen} sx={{ mr: 1 }}>
            <Badge badgeContent={unreadCount} color="error">
              <Notifications />
            </Badge>
          </IconButton>
        </PortalTooltip>

        <Popover
          open={Boolean(notifAnchorEl)}
          anchorEl={notifAnchorEl}
          onClose={handleNotifClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Box sx={{ width: 360, maxHeight: 400, overflow: 'auto' }}>
            <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Notifications {unreadCount > 0 && `(${unreadCount})`}
              </Typography>
              {notifications.length > 0 && (
                <Button
                  size="small"
                  startIcon={<ClearAll />}
                  onClick={clearAllNotifications}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  Clear All
                </Button>
              )}
            </Box>
            {notifications.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">No notifications</Typography>
              </Box>
            ) : (
              <List dense disablePadding>
                {notifications.slice(0, 20).map((n) => (
                  <ListItem
                    key={n.id}
                    button
                    onClick={() => { markAsRead(n.id); }}
                    sx={{
                      bgcolor: n.is_read ? 'transparent' : 'action.hover',
                      borderBottom: '1px solid #f0f0f0',
                    }}
                  >
                    {!n.is_read && (
                      <ListItemIcon sx={{ minWidth: 24 }}>
                        <Circle sx={{ fontSize: 8, color: '#00338D' }} />
                      </ListItemIcon>
                    )}
                    <ListItemText
                      primary={n.title}
                      secondary={
                        <>
                          <Typography variant="caption" component="span" display="block">{n.message}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {n.created_at ? new Date(n.created_at).toLocaleString() : ''}
                          </Typography>
                        </>
                      }
                      sx={{ ml: n.is_read ? 3 : 0 }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </Popover>

        {/* User avatar + menu */}
        <PortalTooltip tooltipKey="navbar.user_menu" placement="bottom">
          <IconButton
            size="large"
            aria-label="user menu"
            aria-controls="user-menu"
            aria-haspopup="true"
            onClick={handleMenuOpen}
            color="inherit"
            disabled={loading}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: '#483698',
                fontSize: '0.875rem',
              }}
            >
              {user?.name ? getInitials(user.name) : <AccountCircle />}
            </Avatar>
          </IconButton>
        </PortalTooltip>

        <Menu
          id="user-menu"
          anchorEl={anchorEl}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          keepMounted
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem disabled>
            <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 200 }}>
              <Typography variant="subtitle2" noWrap>
                <Person sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                {user?.name || 'User'}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {user?.username}
              </Typography>
            </Box>
          </MenuItem>

          {organizationData && (
            <MenuItem disabled>
              <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 200 }}>
                <Typography variant="subtitle2" noWrap>
                  <Business sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                  {organizationData.organizationName}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {organizationData.organizationDomain}
                </Typography>
              </Box>
            </MenuItem>
          )}

          <Divider />

          {hasPortalAccess(userRole || null, 'sme', secondaryRoles) && (
            <MenuItem onClick={() => navigateTo('/sme')}>
              <Assessment sx={{ mr: 2 }} /> KPMG SME Portal
            </MenuItem>
          )}
          {hasPortalAccess(userRole || null, 'em', secondaryRoles) && (
            <MenuItem onClick={() => navigateTo('/em')}>
              <SupervisorAccount sx={{ mr: 2 }} /> KPMG EM Portal
            </MenuItem>
          )}
          {hasPortalAccess(userRole || null, 'admin', secondaryRoles) && (
            <MenuItem onClick={() => navigateTo('/admin')}>
              <AdminPanelSettings sx={{ mr: 2 }} /> KPMG Admin Portal
            </MenuItem>
          )}
          {hasPortalAccess(userRole || null, 'domain_management', secondaryRoles) && (
            <MenuItem onClick={() => navigateTo('/domain-management')}>
              <LibraryBooks sx={{ mr: 2 }} /> Asset Management Portal
            </MenuItem>
          )}

          <Divider />

          <MenuItem onClick={handleLogout} disabled={loading}>
            <Logout sx={{ mr: 2 }} /> Sign Out
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
